import { useEffect, useState } from 'react';
import { createInstance } from '../../util/sdkLoader';
import appSettings from '../../config/settings';

/**
 * Most-mentioned listing category within a single country.
 *
 * Drives the search hero's first trust pill: with no country selected the pill
 * shows the static brand figure ("453 Partner"); once a country is chosen it
 * shows this country's dominant cause (e.g. "Nature conservation").
 *
 * The keys below are the publicData.category slugs of the taxonomy — the same
 * five CategoryStrip filters on, so the cause named in the pill is always one
 * the visitor can then click. Order is global prevalence, used only as the
 * tie-break when two categories are equally common.
 *
 * Counts are fetched via a lightweight anonymous SDK query (perPage:1; we only
 * read meta.totalItems) and cached per country at module scope, so re-visiting a
 * country doesn't refetch.
 */
const CATEGORIES = [
  { key: 'kultur-soziales', labelId: 'filter.category.culture' },
  { key: 'aktiv-abenteuer', labelId: 'filter.category.adventure' },
  { key: 'naturschutz', labelId: 'filter.category.environment' },
  { key: 'meeresschutz', labelId: 'filter.category.marine' },
  { key: 'tierschutz', labelId: 'filter.category.animalCare' },
];

// countryCode -> { key, labelId } | null  (null = country has no categorised results)
const cacheByCountry = new Map();
const inFlightByCountry = new Map();

const fetchTopCategory = countryCode => {
  if (cacheByCountry.has(countryCode)) {
    return Promise.resolve(cacheByCountry.get(countryCode));
  }
  if (inFlightByCountry.has(countryCode)) {
    return inFlightByCountry.get(countryCode);
  }
  if (!appSettings.sdk.clientId) {
    return Promise.resolve(null);
  }

  const sdk = createInstance({
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    ...(appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {}),
    ...(appSettings.sdk.assetCdnBaseUrl
      ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
      : {}),
  });

  // Minimal payload: we only need meta.totalItems. `pub_country` is the same
  // filter the main search applies (SearchPage.duck), so counts match results.
  const baseQuery = { perPage: 1, 'fields.listing': ['title'], 'fields.image': [] };
  // `category` is a multi-enum ARRAY, so the value needs the `has_any:` operator
  // — a bare `pub_category=<slug>` matches nothing (same wiring as CategoryStrip).
  const totalForCategory = category =>
    sdk.listings
      .query({ ...baseQuery, pub_country: countryCode, pub_category: `has_any:${category}` })
      .then(res => res?.data?.meta?.totalItems ?? 0)
      .catch(() => 0);

  const promise = Promise.all(CATEGORIES.map(c => totalForCategory(c.key)))
    .then(counts => {
      let top = null;
      let topCount = 0;
      CATEGORIES.forEach((category, i) => {
        if (counts[i] > topCount) {
          topCount = counts[i];
          top = category;
        }
      });
      cacheByCountry.set(countryCode, top);
      inFlightByCountry.delete(countryCode);
      return top;
    })
    .catch(() => {
      inFlightByCountry.delete(countryCode);
      return null;
    });

  inFlightByCountry.set(countryCode, promise);
  return promise;
};

/**
 * @param {?string} countryCode ISO country code from the `country` URL param, or
 *   a falsy value when no country is selected.
 * @returns {?{key: string, labelId: string}} the most-mentioned category, or null
 *   when no country is selected / counts are still loading / there are no results.
 */
export const useTopCountryCategory = countryCode => {
  const [top, setTop] = useState(() =>
    countryCode && cacheByCountry.has(countryCode) ? cacheByCountry.get(countryCode) : null
  );

  useEffect(() => {
    if (!countryCode) {
      setTop(null);
      return undefined;
    }
    let mounted = true;
    // Show the cached result for this country immediately (no flash); otherwise
    // fall back to null (→ the static pill) until the counts resolve.
    setTop(cacheByCountry.has(countryCode) ? cacheByCountry.get(countryCode) : null);
    fetchTopCategory(countryCode).then(result => {
      if (mounted) {
        setTop(result);
      }
    });
    return () => {
      mounted = false;
    };
  }, [countryCode]);

  return top;
};

export default useTopCountryCategory;
